import { useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { MAX_VENDOR_RECIPIENTS } from '../../../services/vendors';
import { MODAL_INPUT_CLASSES } from '../../shared/ModalField';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirrors the backend validator: emails, no duplicates, capped. Junk stored
// here reads as "configured" while dispatch still blocks.
function recipientRejectionReason(value: string, existing: readonly string[]): string | null {
    const address = value.trim().toLowerCase();
    if (address.length === 0) return null;
    if (!EMAIL_PATTERN.test(address)) return 'Enter a valid email address.';
    if (existing.includes(address)) return 'That recipient is already on the list.';
    if (existing.length >= MAX_VENDOR_RECIPIENTS) {
        return `A vendor can have at most ${MAX_VENDOR_RECIPIENTS} recipients.`;
    }
    return null;
}

interface VendorRecipientsFieldProps {
    recipients: string[];
    disabled?: boolean;
    onChange: (recipients: string[]) => void;
}

export function VendorRecipientsField({
    recipients,
    disabled = false,
    onChange
}: VendorRecipientsFieldProps) {
    const [draft, setDraft] = useState('');
    const [error, setError] = useState<string | null>(null);

    const add = () => {
        const address = draft.trim().toLowerCase();
        if (address.length === 0) return;
        const rejection = recipientRejectionReason(address, recipients);
        if (rejection) {
            setError(rejection);
            return;
        }
        onChange([...recipients, address]);
        setDraft('');
        setError(null);
    };

    const remove = (address: string) => {
        onChange(recipients.filter((entry) => entry !== address));
    };

    return (
        <div className="flex flex-col gap-2">
            {recipients.length === 0 ? (
                <p className="text-sm text-red-500 font-medium">
                    At least one recipient is required to save an email method.
                </p>
            ) : (
                <ul className="flex flex-col gap-1.5">
                    {recipients.map((address) => (
                        <li
                            key={address}
                            className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 bg-white"
                        >
                            <span className="text-sm text-gray-700 truncate" title={address}>
                                {address}
                            </span>
                            <button
                                type="button"
                                onClick={() => remove(address)}
                                disabled={disabled}
                                aria-label={`Remove ${address}`}
                                className="ml-auto text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <div className="flex items-start gap-2">
                <div className="flex-1">
                    <input
                        type="email"
                        value={draft}
                        disabled={disabled || recipients.length >= MAX_VENDOR_RECIPIENTS}
                        onChange={(e) => {
                            setDraft(e.target.value);
                            setError(null);
                        }}
                        onKeyDown={(e) => {
                            if (e.key !== 'Enter') return;
                            e.preventDefault();
                            add();
                        }}
                        placeholder="orders@vendor.com"
                        aria-label="Add a purchase order recipient"
                        className={MODAL_INPUT_CLASSES}
                    />
                    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
                </div>
                <button
                    type="button"
                    onClick={add}
                    disabled={disabled || draft.trim().length === 0}
                    className="flex items-center gap-1.5 bg-white text-gray-800 border border-gray-300 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                >
                    <PlusIcon className="h-4 w-4" />
                    Add
                </button>
            </div>
        </div>
    );
}
